import { asArray, fetchJSON as fetchAPI } from './client';
import { getScopeHeaders } from '../lib/auth-storage';

export type ConnectionSummary = {
  id: string;
  seller_ref: string;
  workspace_id: string;
  status: string;
  healthy: boolean;
  marketplace_count: number;
  credential_id?: string;
  token_expiring: boolean;
  last_sync_at?: string;
  last_error?: string;
};

export type DashboardSummary = {
  tenant_id: string;
  total_connections: number;
  approved_connections: number;
  pending_connections: number;
  failed_connections: number;
  healthy_connections: number;
  active_sync_jobs: number;
  unread_notifications: number;
  open_errors: number;
  connections?: ConnectionSummary[];
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type ApplicationConfig = {
  id: string;
  name: string;
  client_id: string;
  environment: string;
  status?: string;
  version?: number;
  marketplace_id?: string;
  region?: string;
  sp_api_app_id?: string;
  publication_status?: 'draft' | 'published';
  redirect_uris: string[];
  metadata?: Record<string, string>;
  has_client_secret?: boolean;
};

export type ConfigurationAuditEntry = {
  id: string;
  tenant_id: string;
  application_id?: string;
  connection_id?: string;
  action: string;
  actor?: string;
  detail?: Record<string, unknown>;
  created_at: string;
};

export type ConnectionHealthSummary = {
  connection_id: string;
  connected: boolean;
  marketplace_id?: string;
  marketplace_name?: string;
  country_code?: string;
  seller_name?: string;
  seller_id?: string;
  merchant_id?: string;
  seller_flex_site_codes?: string[];
  identity_complete?: boolean;
  connection_age?: string;
  connected_at?: string;
  token_expires_at?: string;
  last_sync_at?: string;
  sync_status?: string;
  health: string;
  status: string;
};

export type CatalogProduct = {
  sku: string;
  asin?: string;
  parent_asin?: string;
  child_asin?: string;
  title?: string;
  brand?: string;
  product_type?: string;
  category?: string;
  status?: unknown;
  price?: string;
  currency?: string;
  fulfilment?: string;
  marketplace_id?: string;
  variation?: unknown;
  inventory_reference?: unknown;
  inventory?: unknown;
  image_url?: string;
  updated_at?: string;
  source_raw_id?: string;
  field_sources?: Record<string, string>;
  payload?: Record<string, unknown>;
};

export type CatalogProductPage = {
  products: CatalogProduct[];
  count: number;
  returned_count?: number;
  total_count: number;
  page: number;
  page_size: number;
  pages: number;
  remaining: number;
  has_more: boolean;
  execution_time_ms?: number;
  q?: string;
  sort?: string;
  order?: string;
  source?: string;
};

export type InventoryRow = {
  asin?: string;
  sku: string;
  product?: string;
  fnsku?: string;
  marketplace_id?: string;
  available: number;
  reserved: number;
  inbound: number;
  fulfilled: number;
  fba_inventory?: number | null;
  mfn_inventory?: number | null;
  seller_flex_inventory?: number | null;
  transit_inventory?: number | null;
  total_inventory?: number | null;
  bsr?: number | null;
  bsr_sc?: number | null;
  sales_30d?: number | null;
  sales_prev_7d?: number | null;
  sales_last_7d?: number | null;
  field_sources?: Record<string, string>;
  last_sync?: string;
  condition?: string;
  payload?: Record<string, unknown>;
};

export type InventoryPage = {
  items: InventoryRow[];
  count: number;
  returned_count?: number;
  total_count: number;
  page: number;
  page_size: number;
  pages: number;
  remaining: number;
  has_more: boolean;
  execution_time_ms?: number;
  q?: string;
  sort?: string;
  order?: string;
  source?: string;
};

export type OrderRow = {
  amazon_order_id: string;
  purchase_date?: string;
  purchase_date_ist?: string;
  order_status?: string;
  return_status?: string;
  return_date?: string;
  asin?: string;
  sku?: string;
  quantity: number;
  order_total?: string;
  currency?: string;
  fulfilment?: string;
  buyer_country?: string;
  marketplace_id?: string;
  last_sync?: string;
  source_raw_id?: string;
  field_sources?: Record<string, string>;
  payload?: Record<string, unknown>;
};

/** SP-API Sync V2 order header (spapi_v2_orders). */
export type SpapiV2Order = {
  connection_id?: string;
  tenant_id?: string;
  amazon_order_id: string;
  purchase_date?: string;
  last_update_date?: string;
  order_status?: string;
  order_type?: string;
  sales_channel?: string;
  fulfillment_channel?: string;
  ship_service_level?: string;
  shipment_service_level_category?: string;
  payment_method?: string;
  payment_method_details?: string[];
  marketplace_id?: string;
  currency?: string;
  amount?: number;
  amount_raw?: string;
  items_shipped?: number;
  items_unshipped?: number;
  is_prime?: boolean;
  is_business_order?: boolean;
  seller_order_id?: string;
  synced_at?: string;
};

/** SP-API Sync V2 order line (spapi_v2_order_items). */
export type SpapiV2OrderItem = {
  connection_id?: string;
  tenant_id?: string;
  amazon_order_id: string;
  order_item_id: string;
  asin?: string;
  seller_sku?: string;
  title?: string;
  qty_ordered?: number;
  qty_shipped?: number;
  item_price?: number;
  item_price_raw?: string;
  currency?: string;
  order_status?: string;
  purchase_date?: string;
  synced_at?: string;
};

export type OrderPage = {
  items: OrderRow[];
  count: number;
  returned_count?: number;
  total_count: number;
  page: number;
  page_size: number;
  pages: number;
  remaining: number;
  has_more: boolean;
  execution_time_ms?: number;
  q?: string;
  sort?: string;
  order?: string;
  source?: string;
};

/** Gate A KPI totals — Sales / Order items / Units (not BME). */
export type OrdersMetrics = {
  connection_id: string;
  from: string;
  to: string;
  timezone: string;
  sales: number;
  order_items: number;
  units: number;
  complete: boolean;
  incomplete_reason?: string;
  currency?: string;
  orders?: number;
  source?: 'all_orders_flat_file' | 'orders_api' | string;
  cod_sales?: number;
  prepaid_sales?: number;
  unclassified_sales?: number;
  cod_items?: number;
  prepaid_items?: number;
  unclassified_items?: number;
  classified_orders?: number;
};

export type OrdersMetricsBucket = {
  start: string;
  sales: number;
  order_items: number;
  units: number;
  orders?: number;
};

export type OrdersMetricsSeries = {
  connection_id: string;
  from: string;
  to: string;
  timezone: string;
  grain: 'day' | 'hour' | string;
  source?: string;
  currency?: string;
  buckets: OrdersMetricsBucket[];
};

export type AcquisitionReportRow = {
  entity_id: string;
  domain: string;
  operation?: string;
  source_raw_id?: string;
  payload?: Record<string, unknown>;
};

export type AcquisitionReportPage = {
  domain: string;
  report_type?: string;
  items: AcquisitionReportRow[];
  count: number;
  returned_count?: number;
  total_count: number;
  page: number;
  page_size: number;
  pages: number;
  has_more: boolean;
  source?: string;
};

export type AllOrdersCityMetric = {
  city: string;
  state: string;
  orders: number;
  units: number;
  sales: number;
};

export type AllOrdersGeo = {
  connection_id: string;
  from: string;
  to: string;
  timezone: string;
  currency?: string;
  cities: AllOrdersCityMetric[];
  states: AllOrdersCityMetric[];
  source: string;
  line_count: number;
};

/**
 * PHASE-A-IMPLEMENTATION-000001 G01 — one Amazon order line (getOrderItems).
 * joins carries the resolvable link targets: order/finance by order id, product/pricing/inventory by SKU.
 */
export type OrderItemRow = {
  order_item_id: string;
  amazon_order_id: string;
  asin?: string;
  sku?: string;
  title?: string;
  purchase_date?: string;
  purchase_date_ist?: string;
  order_status?: string;
  fulfilment?: string;
  quantity_ordered: number;
  quantity_shipped: number;
  currency?: string;
  item_price?: string;
  unit_price?: string;
  item_tax?: string;
  promotion_discount?: string;
  marketplace_id?: string;
  condition?: string;
  last_sync?: string;
  source_raw_id?: string;
  joins?: Record<string, string>;
  field_sources?: Record<string, string>;
  payload?: Record<string, unknown>;
};

export type OrderItemPage = {
  items: OrderItemRow[];
  count: number;
  returned_count?: number;
  total_count: number;
  page: number;
  page_size: number;
  pages: number;
  remaining: number;
  has_more: boolean;
  execution_time_ms?: number;
  q?: string;
  sort?: string;
  order?: string;
  source?: string;
};

/** FINANCE-FOUNDATION-000001 — immutable MarketSync finance_event row. */
export type FinanceEventRow = {
  event_id: string;
  event_type?: string;
  transaction_type?: string;
  amount?: string;
  currency?: string;
  posted_date?: string;
  reference?: string;
  order_id?: string;
  settlement_id?: string;
  fee_type?: string;
  marketplace_id?: string;
  raw_reference?: string;
  version?: number;
  source_raw_id?: string;
  payload?: Record<string, unknown>;
};

export type FinanceEventPage = {
  items: FinanceEventRow[];
  count: number;
  returned_count?: number;
  total_count: number;
  page: number;
  page_size: number;
  pages: number;
  remaining: number;
  has_more: boolean;
  execution_time_ms?: number;
  q?: string;
  sort?: string;
  order?: string;
  source?: string;
};

/** PRICING-FOUNDATION + WORKSPACE-000001 — MarketSync pricing_record row. */
export type PricingRow = {
  asin?: string;
  sku?: string;
  listing_price?: string;
  currency?: string;
  shipping_price?: string;
  landed_price?: string;
  buy_box_winner?: boolean;
  displayed_price?: string;
  bsr?: number | null;
  bsr_sc?: number | null;
  sales_30d?: number | null;
  sales_prev_7d?: number | null;
  sales_last_7d?: number | null;
  bb_price?: string;
  buy_box_pct?: string;
  offers?: number | null;
  buy_box_winner_name?: string;
  field_sources?: Record<string, string>;
  condition?: string;
  status?: string;
  effective_at?: string;
  marketplace_id?: string;
  raw_reference?: string;
  version?: number;
  source_raw_id?: string;
  payload?: Record<string, unknown>;
};

export type PricingPage = {
  items: PricingRow[];
  count: number;
  returned_count?: number;
  total_count: number;
  page: number;
  page_size: number;
  pages: number;
  remaining: number;
  has_more: boolean;
  execution_time_ms?: number;
  q?: string;
  sort?: string;
  order?: string;
  source?: string;
};

/** BUSINESS-METRICS-FOUNDATION-000001 — rollup from business_metrics_daily (never fabricated). */
export type BusinessMetricSummary = {
  connection_id: string;
  marketplace_id?: string;
  asin?: string;
  sku?: string;
  units_sold_30d: number;
  units_sold_60d: number;
  units_sold_90d: number;
  gross_revenue_30d?: number;
  gross_revenue_60d?: number;
  gross_revenue_90d?: number;
  order_count_30d?: number;
  sales_last_7d?: number;
  sales_prev_7d?: number;
  trend_pct?: number;
  trend_direction?: 'up' | 'down' | 'flat' | string;
  asp_30d?: number | null;
  net_revenue_30d?: number;
  fba_inventory?: number | null;
  product_health?: string;
  field_sources?: Record<string, string>;
  as_of?: string;
  source?: string;
};

export type BusinessMetricsPage = {
  items: BusinessMetricSummary[];
  count: number;
  returned_count?: number;
  total_count: number;
  page: number;
  page_size: number;
  pages: number;
  remaining: number;
  has_more: boolean;
  execution_time_ms?: number;
  q?: string;
  sort?: string;
  as_of?: string;
  source?: string;
};

export type BusinessMetricsStatus = {
  engine?: string;
  snapshot_date?: string;
  records?: number;
  coverage_window?: string;
  source?: string;
  last_update?: string;
  calculation_time_ms?: number;
  last_run_status?: string;
  rows_processed?: number;
  rows_updated?: number;
  rows_skipped?: number;
  errors?: number;
  warnings?: number;
  coverage?: Record<string, unknown>;
};

export type BusinessMetricsRun = {
  id: string;
  connection_id: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  rows_processed?: number;
  rows_updated?: number;
  rows_skipped?: number;
  errors?: number;
  warnings?: number;
  status?: string;
  message?: string;
  coverage?: Record<string, unknown>;
};

/** AMAZON-DISCOVERY-LAB-000001 */
export type DiscoveryStatus = {
  engine?: string;
  connection_id?: string;
  last_run?: DiscoveryRun | null;
  endpoints_total: number;
  endpoints_success: number;
  endpoints_permission_issues: number;
  endpoints_error: number;
  endpoints_skipped: number;
  endpoints_blocked?: Record<string, number>;
  reports_cataloged: number;
  reports_executed?: number;
  fields_count: number;
  relationships_count: number;
  samples_count: number;
  schemas_count?: number;
  docs_generated: number;
  documentation_path?: string;
  coverage_pct?: number;
  avg_quality_score?: number;
  schema_status?: string;
  download_status?: string;
  relationship_status?: string;
  documentation_status?: string;
  families?: Record<string, number>;
  note?: string;
};

export type DiscoveryRun = {
  id: string;
  connection_id: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  endpoints_total?: number;
  endpoints_ok?: number;
  endpoints_denied?: number;
  endpoints_error?: number;
  endpoints_skipped?: number;
  reports_cataloged?: number;
  fields_discovered?: number;
  relationships?: number;
  docs_generated?: number;
  status?: string;
  message?: string;
  coverage?: Record<string, unknown>;
};

export type DiscoveryEndpointRow = {
  endpoint_key: string;
  family: string;
  official_name?: string;
  purpose?: string;
  http_method?: string;
  path_template?: string;
  operation?: string;
  last_status: string;
  http_status?: number;
  execution_ms?: number;
  payload_hash?: string;
  error_class?: string;
  error_message?: string;
  how_to_unblock?: string;
  warehouse_recommendation?: string;
  probeable?: boolean;
  quality_score?: number;
  has_sample?: boolean;
  has_schema?: boolean;
  param_sources?: Record<string, string>;
  business_opportunities?: Record<string, string>;
  feature_suggestions?: string[];
  updated_at?: string;
  amazon_request_id?: string;
};

export type DiscoveryReportRow = {
  report_type: string;
  official_name?: string;
  purpose?: string;
  response_format?: string;
  last_status: string;
  error_class?: string;
  warehouse_recommendation?: string;
  feature_suggestions?: string[];
};

export type DiscoveryField = {
  field_path: string;
  original_name?: string;
  data_type?: string;
  sample_value?: string;
  appears_in?: string[];
  potential_business_meaning?: string;
  potential_relationships?: string[];
};

export type DiscoveryRelationship = {
  key_name: string;
  endpoints?: string[];
  field_paths?: string[];
  strength?: number;
};

export type DiscoverySampleMeta = {
  id: string;
  endpoint_key?: string;
  report_type?: string;
  status_code: number;
  payload_hash?: string;
  body_truncated?: boolean;
  body_bytes?: number;
  amazon_request_id?: string;
  captured_at?: string;
};

export type DiscoveryQualityScore = {
  endpoint_key: string;
  executable?: number;
  schema?: number;
  samples?: number;
  relationships?: number;
  documentation?: number;
  dependencies?: number;
  warehouse_ready?: number;
  business_ready?: number;
  overall: number;
};

export type DiscoveryExecutionGraph = {
  connection_id?: string;
  run_id?: string;
  nodes?: { endpoint_key: string; status: string; depends_on?: string[]; provides?: string[]; quality_score?: number }[];
  edges?: [string, string][];
};

export type DiscoveryRecipeStep = {
  order: number;
  action: string;
  api?: string;
  extract?: string;
  notes?: string;
};

export type DiscoveryRecipe = {
  endpoint_key: string;
  family?: string;
  purpose?: string;
  http_method?: string;
  path?: string;
  authentication?: string;
  permissions?: string;
  depends_on?: string[];
  provides?: string[];
  steps?: DiscoveryRecipeStep[];
  param_sources?: Record<string, string>;
  postman_collection?: string;
  postman_recipe?: string;
  postman_url?: string;
  official_docs_url?: string;
  postman_diff_notes?: string[];
  how_to_unblock?: string;
  known_issues?: string[];
  can_be_used_for?: string[];
  expected_lab_status?: string;
  typical_workflow?: string;
};

export type DiscoveryUnresolvedAPI = {
  api_name: string;
  family?: string;
  current_status: string;
  reason?: string;
  how_to_unblock?: string;
  dependencies?: string[];
  authentication_needed?: string;
  required_parameters?: string[];
  expected_response?: string;
  official_postman_recipe?: string;
  official_documentation?: string;
  current_discovery_implementation?: string;
  difference_analysis?: string;
  next_action?: string;
  priority?: string;
};

export type FieldIntel = {
  field_name: string;
  json_path: string;
  data_type: string;
  nullable?: boolean;
  description?: string;
  example?: string;
  business_meaning?: string;
  used_for?: string;
  future_consumers?: string[];
  transformation_required?: string;
  warehouse_candidate?: string;
  ui_pages?: string[];
  reports?: string[];
  ai_usage?: string;
  confidence?: string;
  availability?: string;
  source_endpoint?: string;
  source_report?: string;
  dependencies?: string[];
};

export type BusinessCapability = {
  name: string;
  uses_apis_reports?: string[];
  produces?: string[];
  future_consumers?: string[];
  warehouse_hint?: string;
  example_fields?: string[];
};

export type FieldLineage = {
  field: string;
  source_amazon?: string;
  steps?: { stage: string; name: string; note?: string }[];
};

export type WarehouseEntityRec = {
  entity: string;
  source_endpoints?: string[];
  fact_table_candidate?: string;
  dimension_table_candidates?: string[];
  primary_key_candidate?: string;
  relationships?: string[];
  refresh_frequency?: string;
  retention?: string;
  partition_strategy?: string;
  index_candidates?: string[];
  future_business_metrics?: string[];
};

export type DiscoveryIntelligence = {
  capabilities?: BusinessCapability[];
  lineages?: FieldLineage[];
  warehouse_blueprint?: WarehouseEntityRec[];
  field_intel?: FieldIntel[];
};

export type DiscoveryEndpointPackage = {
  endpoint_key: string;
  definition?: Record<string, unknown>;
  row?: DiscoveryEndpointRow;
  quality?: DiscoveryQualityScore;
  request_schema?: Record<string, unknown>;
  response_schema?: Record<string, unknown>;
  sample?: {
    body_raw?: string;
    request_params?: Record<string, string>;
    headers?: Record<string, string>;
    status_code?: number;
    amazon_request_id?: string;
    url?: string;
    http_method?: string;
  };
  schema_history?: { diff_summary?: string; added_fields?: string[]; removed_fields?: string[]; changed_types?: string[]; captured_at?: string }[];
  dependencies?: string[];
  related_apis?: string[];
  business_opportunities?: Record<string, string>;
  param_sources?: Record<string, string>;
  live_request_ids?: string[];
  recipe?: DiscoveryRecipe;
  how_to_unblock?: string;
  can_be_used_for?: string[];
  known_issues?: string[];
  authentication?: string;
  postman_url?: string;
  field_intel?: FieldIntel[];
  business_meaning?: string;
  capabilities?: BusinessCapability[];
  field_lineages?: FieldLineage[];
  warehouse_candidates?: WarehouseEntityRec[];
  consumers?: string[];
};

export type DiscoveryExplorerPayload = {
  status: DiscoveryStatus;
  endpoints: DiscoveryEndpointRow[];
  reports: DiscoveryReportRow[];
  fields: DiscoveryField[];
  relationships: DiscoveryRelationship[];
  permissions?: unknown[];
  samples_meta: DiscoverySampleMeta[];
  quality_scores?: DiscoveryQualityScore[];
  execution_graph?: DiscoveryExecutionGraph | null;
  recipes?: DiscoveryRecipe[];
  unresolved?: DiscoveryUnresolvedAPI[];
  lab_mode?: string;
  execution_time_ms?: number;
};

export type SellerConnection = {
  id: string;
  seller_ref: string;
  workspace_id: string;
  application_config_id: string;
  status: string;
  marketplace_ids?: string[];
  marketplaces?: { marketplace_id: string; enabled: boolean; ready: boolean }[];
  credential_id?: string;
  token_expires_at?: string;
  last_sync_at?: string;
  last_error?: string;
  /** Seller identity extras e.g. selling_partner_id, seller_flex_site_codes */
  metadata?: Record<string, string>;
};

export type MarketplaceHealth = {
  marketplace_id: string;
  name: string;
  region: string;
  country?: string;
  enabled: boolean;
  ready: boolean;
  healthy: boolean;
};

export type AuthorizationInitiation = {
  authorization_url: string;
  state: string;
  expires_at: string;
};

export type ActivityEntry = {
  id: string;
  connection_id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type ErrorRecord = {
  id: string;
  connection_id: string;
  code: string;
  message: string;
  severity: string;
  domain?: string;
  retryable: boolean;
  resolved: boolean;
  created_at: string;
};

export type ConnectionNotification = {
  id: string;
  connection_id: string;
  title: string;
  body?: string;
  priority: string;
  read: boolean;
  created_at: string;
};

export type SyncJobRecord = {
  id: string;
  connection_id: string;
  domain: string;
  mode: string;
  state: string;
  progress: number;
  error?: string;
  created_at: string;
  completed_at?: string;
};

export type SPAPILogEntry = {
  id: string;
  job_id: string;
  connection_id: string;
  operation: string;
  status_code: number;
  amazon_request_id?: string;
  correlation_id?: string;
  headers?: Record<string, string>;
  body_bytes: number;
  body_preview?: string;
  received_at: string;
};

export type SyncDashboard = {
  connection_id: string;
  active_jobs: number;
  queued_jobs: number;
  failed_jobs: number;
  completed_jobs: number;
  last_sync_at?: string;
  domains: { domain: string; last_job_state?: string; job_count: number }[];
};

/** COMMERCE-SYNC-FOUNDATION-000001 + BACKFILL */
export type CommerceSyncHealthRow = {
  key: string;
  label: string;
  tier: number;
  frequency: string;
  priority: number;
  marketsync_domain?: string;
  runnable: boolean;
  realtime_eligible: boolean;
  status: string;
  last_sync_at?: string;
  next_sync_at?: string;
  duration_ms?: number;
  records_received: number;
  records_persisted: number;
  records_displayed?: number;
  amazon_request_ids?: string[];
  quality: string;
  quality_detail?: string;
  completeness?: string;
  completeness_detail?: string;
  data_from?: string;
  data_to?: string;
  backfill_reached?: string;
  horizon_reached?: boolean;
  pull_type?: string;
  error?: string;
  retries: number;
  last_job_id?: string;
  depends_on?: string[];
};

export type CommerceSyncLogEntry = {
  id: string;
  domain: string;
  pull_type: string;
  reason: string;
  mode?: string;
  window_from?: string;
  window_to?: string;
  records_received: number;
  records_persisted: number;
  quality?: string;
  completeness?: string;
  job_id?: string;
  error?: string;
  created_at: string;
};

export type CommerceSyncHealth = {
  connection_id: string;
  generated_at: string;
  sprint: string;
  note: string;
  backfill_horizon?: string;
  backfill_interval?: string;
  rate_budget_pct?: number;
  domains: CommerceSyncHealthRow[];
  counts: Record<string, number>;
  recent_logs?: CommerceSyncLogEntry[];
};

export type AuthTestResult = {
  connection_id: string;
  success: boolean;
  has_token: boolean;
  token_valid: boolean;
  message?: string;
  tested_at: string;
};

export type AdminHealthReport = {
  total_connections: number;
  healthy_percent: number;
  active_sync_jobs: number;
  queued_sync_jobs: number;
  failed_sync_jobs: number;
  open_errors: number;
  worker_active: boolean;
};

const API_BASE = import.meta.env.VITE_AMAZON_CONNECT_API ?? '/api/v1/amazon';

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

function amazonAuthHeaders(): HeadersInit {
  return { Accept: 'application/zip, application/json', ...getScopeHeaders() };
}

export type ApplicationPayload = {
  name: string;
  client_id: string;
  client_secret?: string;
  environment?: string;
  redirect_uris: string[];
  marketplace_id?: string;
  region?: string;
  sp_api_app_id?: string;
  publication_status?: 'draft' | 'published';
  metadata?: Record<string, string>;
};

function normalizeDomainPage<T extends { items?: unknown[]; count?: number; returned_count?: number; total_count?: number; page?: number; page_size?: number; pages?: number; remaining?: number; has_more?: boolean; execution_time_ms?: number; q?: string; sort?: string; order?: string; source?: string }>(
  res: T | null | undefined,
  _itemsKey: 'items',
) {
  const count = res?.count ?? res?.returned_count ?? 0;
  const total = res?.total_count ?? count;
  const page = res?.page ?? 1;
  const pageSize = res?.page_size ?? 25;
  const pages = res?.pages ?? (pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1);
  const remaining = res?.remaining ?? Math.max(0, total - (page - 1) * pageSize - count);
  return {
    items: asArray(res?.items),
    count,
    returned_count: res?.returned_count ?? count,
    total_count: total,
    page,
    page_size: pageSize,
    pages,
    remaining,
    has_more: res?.has_more ?? remaining > 0,
    execution_time_ms: res?.execution_time_ms,
    q: res?.q,
    sort: res?.sort,
    order: res?.order,
    source: res?.source,
  };
}

export const amazonConnectApi = {
  dashboard: (workspaceId?: string) =>
    fetchJSON<DashboardSummary>(`/connect/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  listApplications: async () => asArray<ApplicationConfig>(await fetchJSON('/applications')),
  getApplication: (id: string) => fetchJSON<ApplicationConfig>(`/applications/${id}`),
  createApplication: (body: ApplicationPayload) =>
    fetchJSON<ApplicationConfig>('/applications', { method: 'POST', body: JSON.stringify(body) }),
  updateApplication: (id: string, body: Partial<ApplicationPayload>) =>
    fetchJSON<ApplicationConfig>(`/applications/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteApplication: (id: string) =>
    fetchJSON<{ message: string }>(`/applications/${id}`, { method: 'DELETE' }),
  activateApplication: (id: string) =>
    fetchJSON<ApplicationConfig>(`/applications/${id}/activate`, { method: 'POST' }),
  disableApplication: (id: string) =>
    fetchJSON<ApplicationConfig>(`/applications/${id}/disable`, { method: 'POST' }),
  duplicateApplication: (id: string) =>
    fetchJSON<ApplicationConfig>(`/applications/${id}/duplicate`, { method: 'POST' }),
  listApplicationAudit: async (id: string) =>
    asArray<ConfigurationAuditEntry>(await fetchJSON(`/applications/${id}/audit`)),
  oauthCallback: (body: {
    connection_id: string;
    state: string;
    code: string;
    redirect_uri?: string;
    selling_partner_id?: string;
  }) => fetchJSON<{ connection_id: string; status: string }>('/oauth/callback', { method: 'POST', body: JSON.stringify(body) }),
  configurationHealth: (id: string) =>
    fetchJSON<{
      application_id: string;
      status: string;
      score: number;
      checks: { field: string; status: string; message?: string; why?: string; how_to_fix?: string; expected?: string }[];
    }>(`/applications/${id}/configuration-health`),
  testConnection: (id: string) =>
    fetchJSON<{ application_id: string; success: boolean; score: number; message?: string }>(`/applications/${id}/test-connection`, {
      method: 'POST',
    }),
  connectionHealth: (connectionId: string) =>
    fetchJSON<ConnectionHealthSummary>(`/connections/${connectionId}/health`),
  sellerVerification: (connectionId: string) =>
    fetchJSON<{
      connection_id: string;
      success: boolean;
      seller_name?: string;
      marketplace_id?: string;
      marketplace_name?: string;
      country_code?: string;
      message?: string;
    }>(`/connections/${connectionId}/seller-verification`),
  listConnections: async (workspaceId?: string) =>
    asArray<SellerConnection>(
      await fetchJSON(`/connections${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
    ),
  requestConnection: (body: {
    workspace_id: string;
    seller_ref: string;
    application_config_id: string;
    marketplace_ids?: string[];
  }) => fetchJSON<SellerConnection>('/connections', { method: 'POST', body: JSON.stringify(body) }),
  initiateAuth: (id: string, redirectUri: string, regionCode?: string) =>
    fetchJSON<AuthorizationInitiation>(`/connections/${id}/authorize`, {
      method: 'POST',
      body: JSON.stringify({ redirect_uri: redirectUri, region_code: regionCode ?? 'NA' }),
    }),
  restartAuthorization: (id: string, redirectUri: string, regionCode?: string) =>
    fetchJSON<AuthorizationInitiation>(`/connections/${id}/restart-authorization`, {
      method: 'POST',
      body: JSON.stringify({ redirect_uri: redirectUri, region_code: regionCode ?? 'NA' }),
    }),
  /** Amazon Self Authorization — import LWA refresh token (Atzr|…). Never log the token. */
  importRefreshToken: (id: string, refreshToken: string, sellingPartnerId?: string) =>
    fetchJSON<SellerConnection>(`/connections/${id}/import-refresh-token`, {
      method: 'POST',
      body: JSON.stringify({
        refresh_token: refreshToken,
        ...(sellingPartnerId?.trim() ? { selling_partner_id: sellingPartnerId.trim() } : {}),
      }),
    }),
  /** Persist Amazon Selling Partner / Merchant Token for catalog SP-API path. */
  setSellerIdentity: (
    id: string,
    body: {
      selling_partner_id: string;
      seller_name?: string;
      seller_flex_site_codes?: string[];
      seller_flex_site_codes_csv?: string;
    },
  ) =>
    fetchJSON<SellerConnection>(`/connections/${id}/seller-identity`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Durable PostgreSQL catalog products (latest completed MarketSync catalog job). */
  listCatalogProducts: async (id: string, limit = 100) => {
    const page = await amazonConnectApi.listCatalogProductsPage(id, { page: 1, page_size: limit });
    return page.products;
  },
  listCatalogProductsPage: async (
    id: string,
    opts?: {
      page?: number;
      page_size?: number;
      q?: string;
      sort?: string;
      order?: 'asc' | 'desc';
      status?: string;
      limit?: number;
    },
  ) => {
    const params = new URLSearchParams();
    if (opts?.page) params.set('page', String(opts.page));
    if (opts?.page_size) params.set('page_size', String(opts.page_size));
    else if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.q) params.set('search', opts.q);
    if (opts?.sort) params.set('sort', opts.sort);
    if (opts?.order) params.set('order', opts.order);
    if (opts?.status) params.set('status', opts.status);
    const qs = params.toString();
    const res = await fetchJSON<CatalogProductPage>(
      `/connections/${id}/catalog/products${qs ? `?${qs}` : ''}`,
    );
    const count = res?.count ?? res?.returned_count ?? 0;
    const total = res?.total_count ?? count;
    const page = res?.page ?? 1;
    const pageSize = res?.page_size ?? 25;
    const pages =
      res?.pages ?? (pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1);
    const remaining =
      res?.remaining ?? Math.max(0, total - (page - 1) * pageSize - count);
    return {
      products: asArray<CatalogProduct>(res?.products),
      count,
      returned_count: res?.returned_count ?? count,
      total_count: total,
      page,
      page_size: pageSize,
      pages,
      remaining,
      has_more: res?.has_more ?? remaining > 0,
      execution_time_ms: res?.execution_time_ms,
      q: res?.q,
      sort: res?.sort,
      order: res?.order,
      source: res?.source,
    } satisfies CatalogProductPage;
  },
  listInventoryPage: async (
    id: string,
    opts?: {
      page?: number;
      page_size?: number;
      q?: string;
      sort?: string;
      order?: 'asc' | 'desc';
      status?: string;
    },
  ) => {
    const params = new URLSearchParams();
    if (opts?.page) params.set('page', String(opts.page));
    if (opts?.page_size) params.set('page_size', String(opts.page_size));
    if (opts?.q) params.set('search', opts.q);
    if (opts?.sort) params.set('sort', opts.sort);
    if (opts?.order) params.set('order', opts.order);
    if (opts?.status) params.set('status', opts.status);
    const qs = params.toString();
    const res = await fetchJSON<InventoryPage>(`/connections/${id}/inventory${qs ? `?${qs}` : ''}`);
    return normalizeDomainPage(res, 'items') as InventoryPage;
  },
  listOrdersPage: async (
    id: string,
    opts?: {
      page?: number;
      page_size?: number;
      q?: string;
      sort?: string;
      order?: 'asc' | 'desc';
      status?: string;
    },
  ) => {
    const params = new URLSearchParams();
    if (opts?.page) params.set('page', String(opts.page));
    if (opts?.page_size) params.set('page_size', String(opts.page_size));
    if (opts?.q) params.set('search', opts.q);
    if (opts?.sort) params.set('sort', opts.sort);
    if (opts?.order) params.set('order', opts.order);
    if (opts?.status) params.set('status', opts.status);
    const qs = params.toString();
    const res = await fetchJSON<OrderPage>(`/connections/${id}/orders${qs ? `?${qs}` : ''}`);
    return normalizeDomainPage(res, 'items') as OrderPage;
  },
  /** Gate A — Sales / Order items / Units for marketplace-local calendar window. */
  getOrdersMetrics: async (
    id: string,
    opts: { from: string; to: string; tz?: string },
  ) => {
    const params = new URLSearchParams();
    params.set('from', opts.from);
    params.set('to', opts.to);
    if (opts.tz) params.set('tz', opts.tz);
    return fetchJSON<OrdersMetrics>(`/connections/${id}/orders/metrics?${params}`);
  },
  getOrdersMetricsSeries: async (
    id: string,
    opts: { from: string; to: string; tz?: string; grain?: 'day' | 'hour' },
  ) => {
    const params = new URLSearchParams();
    params.set('from', opts.from);
    params.set('to', opts.to);
    if (opts.tz) params.set('tz', opts.tz);
    if (opts.grain) params.set('grain', opts.grain);
    return fetchJSON<OrdersMetricsSeries>(`/connections/${id}/orders/metrics/series?${params}`);
  },
  getAllOrdersGeo: async (
    id: string,
    opts: { from: string; to: string; tz?: string },
  ) => {
    const params = new URLSearchParams();
    params.set('from', opts.from);
    params.set('to', opts.to);
    if (opts.tz) params.set('tz', opts.tz);
    return fetchJSON<AllOrdersGeo>(`/connections/${id}/spapi-v2/all-orders/geo?${params}`);
  },
  /** PHASE-A-IMPLEMENTATION-000001 G01 — order lines; order_id narrows to one order. */
  listOrderItemsPage: async (
    id: string,
    opts?: {
      page?: number;
      page_size?: number;
      q?: string;
      sort?: string;
      order?: 'asc' | 'desc';
      order_id?: string;
    },
  ) => {
    const params = new URLSearchParams();
    if (opts?.page) params.set('page', String(opts.page));
    if (opts?.page_size) params.set('page_size', String(opts.page_size));
    if (opts?.q) params.set('search', opts.q);
    if (opts?.sort) params.set('sort', opts.sort);
    if (opts?.order) params.set('order', opts.order);
    if (opts?.order_id) params.set('order_id', opts.order_id);
    const qs = params.toString();
    const res = await fetchJSON<OrderItemPage>(`/connections/${id}/order-items${qs ? `?${qs}` : ''}`);
    return normalizeDomainPage(res, 'items') as OrderItemPage;
  },
  /** FINANCE-FOUNDATION-000001 — MarketSync finance events (PostgreSQL SoT). */
  listFinanceEventsPage: async (
    id: string,
    opts?: {
      page?: number;
      page_size?: number;
      q?: string;
      sort?: string;
      order?: 'asc' | 'desc';
      status?: string;
    },
  ) => {
    const params = new URLSearchParams();
    if (opts?.page) params.set('page', String(opts.page));
    if (opts?.page_size) params.set('page_size', String(opts.page_size));
    if (opts?.q) params.set('search', opts.q);
    if (opts?.sort) params.set('sort', opts.sort);
    if (opts?.order) params.set('order', opts.order);
    if (opts?.status) params.set('status', opts.status);
    const qs = params.toString();
    const res = await fetchJSON<FinanceEventPage>(
      `/connections/${id}/finance/events${qs ? `?${qs}` : ''}`,
    );
    return normalizeDomainPage(res, 'items') as FinanceEventPage;
  },
  /** PRICING-FOUNDATION-000001 — MarketSync pricing records (PostgreSQL SoT). */
  listPricingPage: async (
    id: string,
    opts?: {
      page?: number;
      page_size?: number;
      q?: string;
      sort?: string;
      order?: 'asc' | 'desc';
      status?: string;
    },
  ) => {
    const params = new URLSearchParams();
    if (opts?.page) params.set('page', String(opts.page));
    if (opts?.page_size) params.set('page_size', String(opts.page_size));
    if (opts?.q) params.set('search', opts.q);
    if (opts?.sort) params.set('sort', opts.sort);
    if (opts?.order) params.set('order', opts.order);
    if (opts?.status) params.set('status', opts.status);
    const qs = params.toString();
    const res = await fetchJSON<PricingPage>(`/connections/${id}/pricing${qs ? `?${qs}` : ''}`);
    return normalizeDomainPage(res, 'items') as PricingPage;
  },
  listAcquisitionReports: async (
    id: string,
    domain: string,
    opts?: { page?: number; page_size?: number; q?: string },
  ) => {
    const params = new URLSearchParams();
    if (opts?.page) params.set('page', String(opts.page));
    if (opts?.page_size) params.set('page_size', String(opts.page_size));
    if (opts?.q) params.set('search', opts.q);
    const qs = params.toString();
    const res = await fetchJSON<AcquisitionReportPage>(
      `/connections/${id}/acquisition/reports/${encodeURIComponent(domain)}${qs ? `?${qs}` : ''}`,
    );
    return normalizeDomainPage(res, 'items') as AcquisitionReportPage;
  },
  /** BUSINESS-METRICS-FOUNDATION-000001 — sole derived commerce metrics (reads daily snapshots). */
  listBusinessMetricsPage: async (
    id: string,
    opts?: { page?: number; page_size?: number; q?: string; sort?: string },
  ) => {
    const params = new URLSearchParams();
    if (opts?.page) params.set('page', String(opts.page));
    if (opts?.page_size) params.set('page_size', String(opts.page_size));
    if (opts?.q) params.set('search', opts.q);
    if (opts?.sort) params.set('sort', opts.sort);
    const qs = params.toString();
    const res = await fetchJSON<BusinessMetricsPage>(
      `/connections/${id}/business-metrics${qs ? `?${qs}` : ''}`,
    );
    return normalizeDomainPage(res, 'items') as BusinessMetricsPage;
  },
  businessMetricsStatus: (id: string) =>
    fetchJSON<BusinessMetricsStatus>(`/connections/${id}/business-metrics/status`),
  recomputeBusinessMetrics: (id: string) =>
    fetchJSON<BusinessMetricsRun>(`/connections/${id}/business-metrics/recompute`, { method: 'POST' }),
  /** AMAZON-DISCOVERY-LAB-000001 — Engineering laboratory. */
  getDiscovery: (id: string) => fetchJSON<DiscoveryExplorerPayload>(`/connections/${id}/discovery`),
  discoveryStatus: (id: string) => fetchJSON<DiscoveryStatus>(`/connections/${id}/discovery/status`),
  runDiscovery: (id: string) =>
    fetchJSON<DiscoveryRun>(`/connections/${id}/discovery/run`, { method: 'POST' }),
  runDiscoveryEndpoint: (id: string, key: string) =>
    fetchJSON<DiscoveryRun>(`/connections/${id}/discovery/endpoints/${encodeURIComponent(key)}/run`, {
      method: 'POST',
    }),
  getDiscoveryEndpoint: (id: string, key: string) =>
    fetchJSON<DiscoveryEndpointPackage>(
      `/connections/${id}/discovery/endpoints/${encodeURIComponent(key)}`,
    ),
  discoveryGraph: (id: string) =>
    fetchJSON<DiscoveryExecutionGraph>(`/connections/${id}/discovery/graph`),
  downloadDiscoveryEndpointZip: async (id: string, key: string) => {
    const res = await fetch(
      `${API_BASE}/connections/${id}/discovery/endpoints/${encodeURIComponent(key)}/package`,
      { headers: { ...amazonAuthHeaders() } },
    );
    if (!res.ok) throw new Error(`ZIP download failed: ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${key.replace(/[^\w.-]+/g, '_')}.zip`;
    a.click();
    URL.revokeObjectURL(a.href);
  },
  downloadDiscoveryAllZip: async (id: string) => {
    const res = await fetch(`${API_BASE}/connections/${id}/discovery/package`, {
      headers: { ...amazonAuthHeaders() },
    });
    if (!res.ok) throw new Error(`Download All failed: ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'AmazonDiscovery.zip';
    a.click();
    URL.revokeObjectURL(a.href);
  },
  getDiscoveryIntelligence: (id: string) =>
    fetchJSON<DiscoveryIntelligence>(`/connections/${id}/discovery/intelligence`),
  downloadDiscoveryIntelligenceZip: async (id: string) => {
    const res = await fetch(`${API_BASE}/connections/${id}/discovery/intelligence/package`, {
      headers: { ...amazonAuthHeaders() },
    });
    if (!res.ok) throw new Error(`Intelligence ZIP failed: ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'DiscoveryIntelligence.zip';
    a.click();
    URL.revokeObjectURL(a.href);
  },
  /** DISCOVERY-EXPORT-000001 — Founder planning CSVs */
  downloadDiscoveryFieldMatrixCSV: async (id: string) => {
    const res = await fetch(`${API_BASE}/connections/${id}/discovery/exports/field-matrix`, {
      headers: { ...amazonAuthHeaders() },
    });
    if (!res.ok) throw new Error(`Field Matrix CSV failed: ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'Amazon_API_Field_Matrix.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  },
  downloadDiscoveryFieldMasterCSV: async (id: string) => {
    const res = await fetch(`${API_BASE}/connections/${id}/discovery/exports/field-master`, {
      headers: { ...amazonAuthHeaders() },
    });
    if (!res.ok) throw new Error(`Field Master CSV failed: ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'Amazon_Field_Master.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  },
  deleteConnection: (id: string) =>
    fetchJSON<{ message: string }>(`/connections/${id}`, { method: 'DELETE' }),
  getConnection: (id: string) => fetchJSON<SellerConnection>(`/connections/${id}`),
  approveConnection: (id: string) =>
    fetchJSON<SellerConnection>(`/connections/${id}/approve`, { method: 'POST' }),
  disconnectConnection: (id: string) =>
    fetchJSON<SellerConnection>(`/connections/${id}/disconnect`, { method: 'POST' }),
  reconnectConnection: (id: string) =>
    fetchJSON<SellerConnection>(`/connections/${id}/reconnect`, { method: 'POST' }),
  listMarketplaces: async () => asArray<MarketplaceHealth>(await fetchJSON('/connect/marketplaces')),
  connectionMarketplaceHealth: async (id: string) =>
    asArray<MarketplaceHealth>(await fetchJSON(`/connections/${id}/marketplaces/health`)),
  associateMarketplace: (id: string, marketplaceId: string, enabled = true) =>
    fetchJSON<SellerConnection>(`/connections/${id}/marketplaces`, {
      method: 'POST',
      body: JSON.stringify({ marketplace_id: marketplaceId, enabled }),
    }),
  listActivity: async (id: string) => asArray<ActivityEntry>(await fetchJSON(`/connections/${id}/activity`)),
  listErrors: async (id: string, unresolved = true) =>
    asArray<ErrorRecord>(await fetchJSON(`/connections/${id}/errors?unresolved=${unresolved}`)),
  retryError: (connectionId: string, errorId: string) =>
    fetchJSON<ErrorRecord>(`/connections/${connectionId}/errors/${errorId}/retry`, { method: 'POST' }),
  listNotifications: async (id: string, unread = false) =>
    asArray<ConnectionNotification>(
      await fetchJSON(`/connections/${id}/notifications?unread=${unread}`),
    ),
  authTest: (id: string) =>
    fetchJSON<AuthTestResult>(`/connections/${id}/auth-test`, { method: 'POST' }),
  /** Amazon SP-API communication log — one entry per Amazon HTTP response. */
  listSPAPILog: async (id: string, limit = 50) =>
    asArray<SPAPILogEntry>(await fetchJSON(`/connections/${id}/spapi-log?limit=${limit}`)),
  syncDashboard: (id: string) => fetchJSON<SyncDashboard>(`/connections/${id}/sync`),
  listSyncJobs: async (id: string) => asArray<SyncJobRecord>(await fetchJSON(`/connections/${id}/sync/jobs`)),
  triggerSync: (id: string, domain: string, mode = 'incremental') =>
    fetchJSON<SyncJobRecord>(`/connections/${id}/sync`, {
      method: 'POST',
      body: JSON.stringify({ domain, mode }),
    }),
  triggerFullSync: async (id: string) =>
    asArray<SyncJobRecord>(await fetchJSON(`/connections/${id}/sync/full`, { method: 'POST' })),
  /** COMMERCE-SYNC-FOUNDATION-000001 */
  commerceSyncHealth: (id: string) =>
    fetchJSON<CommerceSyncHealth>(`/connections/${id}/sync/health`),
  commerceRealtimeRefresh: (id: string, domains?: string[]) =>
    fetchJSON<SyncJobRecord[]>(`/connections/${id}/sync/realtime`, {
      method: 'POST',
      body: JSON.stringify({ domains: domains ?? ['orders'] }),
    }),
  /** SP-API Sync V2 greenfield (Path B) */
  spapiV2Identity: (id: string) =>
    fetchJSON<{
      connection_id: string;
      selling_partner_id: string;
      marketplace_id: string;
      identity_complete: boolean;
      certified: boolean;
      certified_at?: string;
      v2_enabled: boolean;
      note: string;
    }>(`/connections/${id}/spapi-v2/identity`),
  spapiV2Certify: (id: string) =>
    fetchJSON<{ certified: boolean; v2_enabled: boolean; selling_partner_id: string }>(
      `/connections/${id}/spapi-v2/identity/certify`,
      { method: 'POST', body: '{}' },
    ),
  spapiV2Sync: (id: string, domain: 'orders' | 'order_items' | 'all_orders', opts?: { ifDue?: boolean }) =>
    fetchJSON<Record<string, unknown>>(`/connections/${id}/spapi-v2/sync`, {
      method: 'POST',
      body: JSON.stringify({ domain, ...(opts?.ifDue ? { if_due: true } : {}) }),
    }),
  spapiV2Orders: (id: string, page = 1, pageSize = 50) =>
    fetchJSON<{ items: SpapiV2Order[]; total: number; page?: number; page_size?: number }>(
      `/connections/${id}/spapi-v2/orders?page=${page}&page_size=${pageSize}`,
    ),
  spapiV2OrderItems: (id: string, orderId?: string, page = 1, pageSize = 50) => {
    const q = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (orderId) q.set('order_id', orderId);
    return fetchJSON<{ items: SpapiV2OrderItem[]; total: number; page?: number; page_size?: number }>(
      `/connections/${id}/spapi-v2/order-items?${q}`,
    );
  },
  adminHealth: () => fetchJSON<AdminHealthReport>('/connect/admin/health'),
  diagnostics: (id: string) => fetchJSON<Record<string, unknown>>(`/connections/${id}/diagnostics`),
  /** DATAFLOW-000001 System Explorer — ownership + live counts (no business features). */
  dataflowExplorer: (connectionId?: string) =>
    fetchJSON<DataflowExplorer>(
      `/dataflow/explorer${connectionId ? `?connection_id=${encodeURIComponent(connectionId)}` : ''}`,
    ),
  dataflowEntityDetail: (entityId: string, connectionId?: string) =>
    fetchJSON<Record<string, unknown>>(
      `/dataflow/entities/${encodeURIComponent(entityId)}${
        connectionId ? `?connection_id=${encodeURIComponent(connectionId)}` : ''
      }`,
    ),
};

export type DataflowExplorer = {
  generated_at: string;
  architecture_version: string;
  program: string;
  cert_sprint: string;
  entities: DataflowEntityRow[];
  database_ownership: DataflowTable[];
  runtime: {
    workers_configured: number;
    queue_jobs_active_or_queued: number;
    dlq_count: number;
    last_error_summary?: string;
    storage_mode: string;
    marketsync_bound: boolean;
  };
  engineering: {
    architecture_version: string;
    platform_version: string;
    git_commit: string;
    deployment_time: string;
    schema_note: string;
    migration_note: string;
    foundation_phase: string;
    certification_status: string;
    storage_mode: string;
  };
  verdicts: Record<string, string>;
  unknown_data_paths: string[];
  sot_violations: string[];
  trace_hint: string;
  connection_id?: string;
};

export type DataflowEntityRow = {
  entity_id: string;
  entity: string;
  amazon_endpoint: string;
  adapter: string;
  parser: string;
  canonical_operation?: string;
  database_tables: string[];
  repository: string;
  service: string;
  rest_api: string;
  frontend_component: string;
  displayed_screen: string;
  sync_frequency: string;
  source_of_truth: string;
  implementation: string;
  marketsync_domain?: string;
  status: string;
  amazon_records?: number;
  raw_records: number;
  canonical_records: number;
  database_records: number;
  api_records?: number;
  displayed_records?: number;
  page_size?: number;
  remaining_records?: number;
  mismatch_reason?: string;
  last_sync_at?: string;
  last_job_state?: string;
  last_job_id?: string;
  last_error?: string;
  last_request_id?: string;
  last_correlation_id?: string;
  owner: string;
  notes?: string;
};

export type DataflowTable = {
  table_name: string;
  purpose: string;
  owner: string;
  primary_key: string;
  who_writes: string;
  who_reads: string;
  temporary: boolean;
  storage: string;
};

export const SYNC_DOMAINS = [
  'catalog',
  'inventory',
  'orders',
  'order_items',
  'finance',
  'advertising',
  'pricing',
] as const;
